import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Tabs } from '@/components/UI/Tabs';
import { Button } from '@/components/UI/Button';
import { Select } from '@/components/UI/Select';
import { Textarea } from '@/components/UI/Textarea';
import { SearchInput } from '@/components/UI/SearchInput';
import { Switch } from '@/components/UI/Switch';
import { Modal } from '@/components/UI/Modal';
import { Alert } from '@/components/UI/Alert';
import { Badge } from '@/components/UI/Badge';
import { LoadingState } from '@/components/UI/LoadingState';
import { EmptyState } from '@/components/UI/EmptyState';
import { Pagination } from '@/components/UI/Pagination';
import { apiErrorMessage } from '@/utils/apiError';
import { ToolErrorEditDialog } from '@/components/ToolErrors/ToolErrorEditDialog';
import {
  useListToolErrorTypesQuery,
  useUpdateToolErrorTypeMutation,
  useListToolErrorsQuery,
  type ToolError,
  type ToolErrorType,
} from '@/redux/internal-services/toolErrorsApi';

type Tab = 'types' | 'errors';
const PAGE_SIZE = 25;

const ToolErrorsPage: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('types');
  const [module, setModule] = useState('');
  const [type, setType] = useState('');
  const [tool, setTool] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const typesQuery = useListToolErrorTypesQuery();
  const errorsQuery = useListToolErrorsQuery({ module, type, tool, search: search.trim(), skip: page * PAGE_SIZE, limit: PAGE_SIZE });
  const totalPages = Math.ceil((errorsQuery.data?.total ?? 0) / PAGE_SIZE);
  // Any filter change starts again from the first page.
  const filtered = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(0); };
  const [updateType, { isLoading: savingType }] = useUpdateToolErrorTypeMutation();

  const types = useMemo(() => typesQuery.data?.data.types ?? [], [typesQuery.data]);
  const modules = typesQuery.data?.data.modules ?? {};
  const toolsByModule = useMemo(() => typesQuery.data?.data.tools_by_module ?? {}, [typesQuery.data]);
  // Tool options follow the Module filter: that module's tools, or every module's tools grouped.
  const toolGroups = useMemo(
    () => Object.entries(toolsByModule).filter(([code]) => !module || code === module),
    [toolsByModule, module],
  );
  const changeModule = (next: string) => {
    setModule(next);
    setPage(0);
    if (tool && next && !(toolsByModule[next] ?? []).includes(tool)) setTool('');
  };
  const typeDefault = useMemo(() => Object.fromEntries(types.map((x) => [x.type, x.retryable])), [types]);
  const errors = errorsQuery.data?.data ?? [];

  const [typeDraft, setTypeDraft] = useState<ToolErrorType | null>(null);
  const [editing, setEditing] = useState<ToolError | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const yesNo = (v: boolean) => (
    <Badge variant={v ? 'success' : 'neutral'}>{v ? t('platform.toolErrors.yes') : t('platform.toolErrors.no')}</Badge>
  );

  const saveType = async () => {
    if (!typeDraft) return;
    setFormError(null);
    try {
      await updateType({
        type: typeDraft.type, meaning: typeDraft.meaning, model_action: typeDraft.model_action, retryable: typeDraft.retryable,
      }).unwrap();
      setTypeDraft(null);
    } catch (e) {
      setFormError(apiErrorMessage(e, t, t('platform.toolErrors.saveFailed')));
    }
  };

  const loadError = (typesQuery.isError && typesQuery.error) || (tab === 'errors' && errorsQuery.isError && errorsQuery.error);

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={<PageHeader title={t('platform.toolErrors.title')} subtitle={t('platform.toolErrors.subtitle')} />}
    >
      <Tabs
        className="mb-4"
        label={t('platform.toolErrors.title')}
        value={tab}
        onChange={(id) => setTab(id as Tab)}
        tabs={[
          { id: 'types', label: t('platform.toolErrors.typesTab'), count: types.length },
          { id: 'errors', label: t('platform.toolErrors.errorsTab'), count: errorsQuery.data?.total },
        ]}
      />
      {loadError && <Alert tone="danger" className="mb-4">{apiErrorMessage(loadError, t, t('platform.toolErrors.loadFailed'))}</Alert>}

      {tab === 'types' && (typesQuery.isLoading ? <LoadingState label={t('platform.toolErrors.loading')} className="py-16" /> : (
        <div className="table-card">
          <div className="no-scrollbar overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr>
                  <th className="th-cell">{t('platform.toolErrors.type')}</th>
                  <th className="th-cell">{t('platform.toolErrors.meaning')}</th>
                  <th className="th-cell">{t('platform.toolErrors.modelAction')}</th>
                  <th className="th-cell">{t('platform.toolErrors.retryable')}</th>
                  <th className="th-cell text-right">{t('platform.toolErrors.errorsTab')}</th>
                  <th className="th-cell" />
                </tr>
              </thead>
              <tbody>
                {types.map((x) => (
                  <tr key={x.type} className="tr-hover">
                    <td className="td-cell"><Badge variant="accent" className="font-mono">{x.type}</Badge></td>
                    <td className="td-cell text-sm text-ui-text">{x.meaning}</td>
                    <td className="td-cell text-sm text-ui-text2">{x.model_action}</td>
                    <td className="td-cell">{yesNo(x.retryable)}</td>
                    <td className="td-cell text-right text-sm">{x.error_count}</td>
                    <td className="td-cell text-right">
                      <Button variant="link" size="xs" onClick={() => { setFormError(null); setTypeDraft({ ...x }); }}>
                        {t('platform.toolErrors.edit')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {tab === 'errors' && (
        <>
          <div className="mb-4 grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              label={t('platform.toolErrors.search')}
              placeholder={t('platform.toolErrors.searchPlaceholder')}
              value={search}
              onChange={(e) => filtered(setSearch)(e.target.value)}
            />
            <Select label={t('platform.toolErrors.module')} value={module} onChange={(e) => changeModule(e.target.value)}>
              <option value="">{t('platform.toolErrors.allModules')}</option>
              {Object.entries(modules).map(([code, name]) => <option key={code} value={code}>{`${code} · ${name}`}</option>)}
            </Select>
            <Select label={t('platform.toolErrors.type')} value={type} onChange={(e) => filtered(setType)(e.target.value)}>
              <option value="">{t('platform.toolErrors.allTypes')}</option>
              {types.map((x) => <option key={x.type} value={x.type}>{x.type}</option>)}
            </Select>
            <Select label={t('platform.toolErrors.tool')} value={tool} onChange={(e) => filtered(setTool)(e.target.value)}>
              <option value="">{t('platform.toolErrors.allTools')}</option>
              {module
                ? (toolsByModule[module] ?? []).map((name) => <option key={name} value={name}>{name}</option>)
                : toolGroups.map(([code, names]) => (
                  <optgroup key={code} label={`${code} · ${modules[code] ?? code}`}>
                    {names.map((name) => <option key={name} value={name}>{name}</option>)}
                  </optgroup>
                ))}
            </Select>
          </div>
          {errorsQuery.isLoading ? <LoadingState label={t('platform.toolErrors.loading')} className="py-16" /> : errors.length === 0 ? (
            <EmptyState
              className="rounded-card border border-dashed border-ui-border py-12"
              title={t('platform.toolErrors.noErrorsTitle')}
              description={t('platform.toolErrors.noErrorsBody')}
            />
          ) : (
            <div className="table-card">
              <div className="no-scrollbar overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr>
                      <th className="th-cell">{t('platform.toolErrors.name')}</th>
                      <th className="th-cell">{t('platform.toolErrors.code')}</th>
                      <th className="th-cell">{t('platform.toolErrors.module')}</th>
                      <th className="th-cell">{t('platform.toolErrors.type')}</th>
                      <th className="th-cell">{t('platform.toolErrors.retryable')}</th>
                      <th className="th-cell">{t('platform.toolErrors.message')}</th>
                      <th className="th-cell">{t('platform.toolErrors.tools')}</th>
                      <th className="th-cell" />
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e) => (
                      <tr key={e.code} className="tr-hover">
                        <td className="td-cell">
                          <p className="font-mono text-xs text-ui-text">{e.name}</p>
                          {!e.is_active && <Badge variant="neutral">{t('platform.toolErrors.inactive')}</Badge>}
                        </td>
                        <td className="td-cell font-mono text-xs text-ui-text2">{e.code}</td>
                        <td className="td-cell text-xs" title={modules[e.module]}>{e.module}</td>
                        <td className="td-cell">
                          <Badge variant="accent" className="font-mono">{e.type}</Badge>
                          {e.type_from_exception && <span className="text-caption text-ui-text3">{t('platform.toolErrors.typeFromException')}</span>}
                        </td>
                        <td className="td-cell">
                          {e.retryable === null ? (
                            <span className="text-caption text-ui-text3">
                              {t('platform.toolErrors.inherits', {
                                value: typeDefault[e.type] ? t('platform.toolErrors.yes') : t('platform.toolErrors.no'),
                              })}
                            </span>
                          ) : yesNo(e.retryable)}
                        </td>
                        <td className="td-cell max-w-md font-mono text-xs text-ui-text2">{e.message}</td>
                        <td className="td-cell">
                          <div className="flex max-w-xs flex-wrap gap-1">
                            {(e.tools ?? []).map((name) => (
                              <Badge key={name} variant={name === tool ? 'accent' : 'neutral'} className="font-mono">{name}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="td-cell text-right">
                          <Button
                            variant="link"
                            size="xs"
                            onClick={() => setEditing(e)}
                          >
                            {t('platform.toolErrors.edit')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <Pagination
            className="mt-3"
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            previousLabel={t('platform.toolErrors.previous')}
            nextLabel={t('platform.toolErrors.next')}
          />
        </>
      )}

      {typeDraft && (
        <Modal
          title={typeDraft.type}
          onClose={() => setTypeDraft(null)}
          onSave={saveType}
          saving={savingType}
          saveLabel={t('platform.toolErrors.save')}
          closeOnBackdrop={false}
        >
          <div className="space-y-3">
            {formError && <Alert tone="danger">{formError}</Alert>}
            <Textarea
              label={t('platform.toolErrors.meaning')}
              value={typeDraft.meaning}
              onChange={(e) => setTypeDraft({ ...typeDraft, meaning: e.target.value })}
            />
            <Textarea
              label={t('platform.toolErrors.modelAction')}
              value={typeDraft.model_action}
              onChange={(e) => setTypeDraft({ ...typeDraft, model_action: e.target.value })}
            />
            <div className="flex items-center justify-between gap-3">
              <span id="type-retryable" className="text-sm text-ui-text">{t('platform.toolErrors.retryableDefault')}</span>
              <Switch
                aria-labelledby="type-retryable"
                checked={typeDraft.retryable}
                onCheckedChange={(next) => setTypeDraft({ ...typeDraft, retryable: next })}
              />
            </div>
          </div>
        </Modal>
      )}

      {editing && <ToolErrorEditDialog error={editing} typeDefault={typeDefault} onClose={() => setEditing(null)} />}
    </PageShell>
  );
};

export default ToolErrorsPage;
